import { useEffect, useState, useContext } from "react";

import AuthContext from "../context/AuthContext";
import SearchBox from "./SearchBox";

import { fetchMyFriends } from "../api/UserAPI";

export default function UserSelectModal({ onSend, onClose }) {
  const { token, user } = useContext(AuthContext);
  const [selectedUserIds, setSelectedUserIds] = useState([]); // array of ids
  const [profilesById, setProfilesById] = useState({}); // { [id]: profile }
  const [friends, setFriends] = useState([]); // profiles from friends API
  const [nextUrl, setNextUrl] = useState(null);
  const [loadingFriends, setLoadingFriends] = useState(false);
  const [friendsError, setFriendsError] = useState(null);

  const mergeUniqueById = (prev, next) => {
    const map = new Map(prev.map((x) => [x.id, x]));
    for (const item of next) map.set(item.id, item);
    return Array.from(map.values());
  };

  const loadFriendsPage = async (url) => {
    if (!token || loadingFriends) return; // guardrail: no token or already loading
    if (url === null) return; // guardrail: no more pages

    setLoadingFriends(true);
    setFriendsError(null);

    try {
      const data = await fetchMyFriends({ token, url });

      const results = Array.isArray(data?.results) ? data.results : [];
      setFriends((prev) => mergeUniqueById(prev, results));
      setNextUrl(data?.next ?? null);

      // Keep lookup map updated (helps selected display)
      setProfilesById((prev) => {
        const next = { ...prev };
        for (const f of results) next[f.id] = f;
        return next;
      });
    } catch (err) {
      setFriendsError("Failed to load friends");
      console.error(err);
    } finally {
      setLoadingFriends(false);
    }
  };

  useEffect(() => {
    if (!token || !user) return;

    // reset when user/token changes
    setFriends([]);
    setNextUrl("/friends/");
    setFriendsError(null);

    loadFriendsPage("/friends/");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, token]);

  useEffect(() => {
    setProfilesById((prev) => {
      const next = { ...prev };
      for (const f of friends) next[f.id] = f;
      return next;
    });
  }, [friends]);

  const loadMoreFriends = () => {
    if (!nextUrl || loadingFriends) return;
    loadFriendsPage(nextUrl);
  };

  const handleSearch = (profile) => {
    if (!profile?.id) return;

    // store profile data for display
    setProfilesById((prev) => ({ ...prev, [profile.id]: profile }));

    // store id (dedupe)
    setSelectedUserIds((prev) => (prev.includes(profile.id) ? prev : [...prev, profile.id]));
  };

  const toggleUser = (profile) => {
    if (!profile?.id) return;

    // ensure we have the profile in the map
    setProfilesById((prev) => ({ ...prev, [profile.id]: profile }));

    setSelectedUserIds(
      (prev) =>
        prev.includes(profile.id)
          ? prev.filter((id) => id !== profile.id) // unselect
          : [...prev, profile.id], // select
    );
  };

  // Selection is keyed by Profile id (`id` in /friends/ and search results),
  // but every caller posts User ids (event/occurrence invites, thread
  // participants) — so hand them `user_id`, never the profile id.
  const handleSend = () => {
    const userIds = selectedUserIds.map((id) => profilesById[id]?.user_id).filter((id) => id != null);
    onSend(userIds);
    onClose();
    // onSend(selectedPartners);
  };

  const selectedUsers = selectedUserIds.map((id) => profilesById[id]).filter(Boolean);

  const visibleFriends = Array.isArray(friends)
    ? friends.filter((f) => !selectedUserIds.includes(f.id))
    : [];

  return (
    // {/* modal main */}
    <div
      className="flex flex-col items-center bg-white max-w-xl min-w-[500px] max-h-[60vh] w-full relative overflow-hidden h-[60vh]"
      onClick={(e) => e.stopPropagation()}
    >
      <SearchBox onItemClick={handleSearch} />
      {friendsError && <div className="text-sm text-red-600 mb-2">{friendsError}</div>}
      {selectedUsers.length > 0 && (
        <ul className="w-2/3 divide-y rounded-lg border mb-2 gap-1">
          {selectedUsers.map((u) => (
            <li
              key={u.id}
              className={`flex items-center gap-3 p-3 cursor-pointer bg-gray`}
              onClick={() => {
                toggleUser(u);
              }} // multi-select behavior
            >
              <img
                src={u.profile_pic_url || `${process.env.PUBLIC_URL}/logo512.png`}
                alt={u.user}
                className="h-9 w-9 rounded-full object-cover border"
              />
              <div className="flex-1">
                <div className="font-medium">{u.username}</div>
              </div>
              {/* checkbox indicator */}
              <input
                type="checkbox"
                checked={true}
                onChange={() => toggleUser(u)}
                onClick={(e) => e.stopPropagation()}
              />
            </li>
          ))}
        </ul>
      )}
      {selectedUsers && selectedUsers.length > 0 && (
        <button
          onClick={handleSend}
          className="bg-blue-500 text-white px-6 py-1 rounded-full hover:bg-blue-400"
        >
          Send
        </button>
      )}
      {/* FRIENDS */}
      <div className="flex items-center gap-3 w-full my-3 pl-3 pr-3">
        <div className="flex-1 border-t border-gray-300" />
        <span className="text-xs uppercase tracking-wide text-gray-500">Friends</span>
        <div className="flex-1 border-t border-gray-300" />
      </div>
      {visibleFriends.length > 0 && (
        <ul className="w-2/3 divide-y rounded-lg border">
          {visibleFriends.map((u) => (
            <li
              key={u.id}
              className={`flex items-center gap-3 p-3 cursor-pointer bg-white`}
              onClick={() => {
                toggleUser(u);
              }}
            >
              <img
                src={u.profile_pic_url || `${process.env.PUBLIC_URL}/logo512.png`}
                alt={u.user}
                className="h-9 w-9 rounded-full object-cover border"
              />
              <div className="flex-1">
                <div className="font-medium">{u.username}</div>
              </div>

              {/* checkbox indicator */}
              <input
                type="checkbox"
                checked={false}
                onChange={() => toggleUser(u)}
                onClick={(e) => e.stopPropagation()}
              />
            </li>
          ))}
        </ul>
      )}
      <div className="mt-3">
        {!loadingFriends && nextUrl && (
          <button
            type="button"
            onClick={loadMoreFriends}
            disabled={!nextUrl || loadingFriends}
            className="w-full border rounded px-3 py-2 hover:bg-gray-50 disabled:opacity-50"
          >
            Load More
          </button>
        )}
      </div>
    </div>
  );
}
